import React, { useState, useEffect } from 'react';
import * as Icons from "../../assets/SVGs/index"; // Assuming your upload and delete icons are in this directory

interface VideoUploadProps {
  onVideoChange: (file: File | null) => void;
  label?: string;
  uploadButtonText?: string;
  storageKey: string; // Unique key for localStorage
  maxFileSize?: number; // Maximum file size in bytes (default: 10 MB)
  defaultVideo?: string; // Add defaultVideo prop
}

const VideoUpload: React.FC<VideoUploadProps> = ({
  onVideoChange,
  label,
  uploadButtonText = "آپلود ویدئو",
  storageKey,
  maxFileSize = 10 * 1024 * 1024, // 10 MB default
  defaultVideo,
}) => {
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Load video from defaultVideo prop or localStorage if available
    const savedVideo = localStorage.getItem(storageKey);
    if (defaultVideo) {
      setSelectedVideo(defaultVideo);
    } else if (savedVideo) {
      setSelectedVideo(savedVideo);
    } else {
      setSelectedVideo(null);
    }

    // Cleanup function to clear localStorage when the component unmounts
    return () => {
      localStorage.removeItem(storageKey);
    };
  }, [defaultVideo, storageKey]);

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check if file size exceeds the maximum limit
      if (file.size > maxFileSize) {
        setError("حجم فایل از حداکثر محدودیت ۱۰ مگابایت بیشتر است.");
        return;
      }
      setError(null);

      // Create a URL for the file instead of base64 encoding
      const videoUrl = URL.createObjectURL(file);
      setSelectedVideo(videoUrl);
      localStorage.setItem(storageKey, videoUrl); // Store the video URL in localStorage
      onVideoChange(file);
    }
  };

  const handleDeleteVideo = () => {
    setSelectedVideo(null);
    onVideoChange(null);
    localStorage.removeItem(storageKey); // Remove video URL from localStorage
  };

  return (
    <div className="w-full">
      {label && <label htmlFor="video" className="block mb-2 text-lg font-medium">{label}</label>}
      <div className="border-dashed border-2 border-[#B2B2B2] p-4 text-center rounded flex justify-center items-center">
        <input
          type="file"
          id="video"
          name="video"
          onChange={handleVideoChange}
          className="hidden"
          accept=".mp4, .mkv, .avi, .mov"
        />
        <label
          htmlFor="video"
          className="cursor-pointer flex items-center justify-center"
        >
          <Icons.VideoUploadIcon className="w-6 h-6 text-[#B2B2B2] ml-3" />
          <span className="ml-2 text-sm text-[#B2B2B2]">{uploadButtonText}</span>
        </label>
      </div>
      {error && <p className="text-red-500 mt-2">{error}</p>}
      {selectedVideo && (
        <div className="mt-4 flex items-center relative">
          <video
            src={selectedVideo}
            controls
            className="w-[80%] h-[200px] mx-auto object-cover border border-[#b2b2b2] rounded"
          />
          <button
            onClick={handleDeleteVideo}
            className="bg-[#FAFAFA] absolute right-1 top-1 rounded p-1"
          >
            <Icons.DeleteIcon className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default VideoUpload;
